import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import type { CampoCliente, PlayResponse } from "@juegossc/shared";
import { useAppSession } from "../hooks/useAppSession";
import { useAppState } from "../hooks/useAppState";
import { useBackgroundStyle } from "../hooks/useBackgroundStyle";
import { appPlay } from "../lib/api";
import { playEvent } from "../lib/audio";
import { FitScreen } from "../components/landing/FitScreen";
import { RuletaView } from "../components/ruleta/RuletaView";
import { Modal } from "../components/ui/Modal";
import { DatosClienteForm } from "../components/ui/DatosClienteForm";
import { PremioModal } from "../components/ui/PremioModal";
import { ModoPruebaBanner } from "../components/ui/Banner";

type Datos = Partial<Record<CampoCliente, string>>;

export default function Ruleta() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSession();
  const { state, loading, error } = useAppState(isAuthenticated);
  // Hook ANTES de los early returns (regla §10 SYSTEM.md). Acepta null
  // mientras state está cargando — devuelve undefined y no hay fondo.
  const bgStyle = useBackgroundStyle(state?.global);

  const [needsDatos, setNeedsDatos] = useState(false);
  const [datos, setDatos] = useState<Datos | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [targetIndex, setTargetIndex] = useState(-1);
  const [resultado, setResultado] = useState<PlayResponse | null>(null);
  const [showPremioModal, setShowPremioModal] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  const [flapperTick, setFlapperTick] = useState(0);
  const [flapperIntensity, setFlapperIntensity] = useState(0);

  const handleTick = useCallback((intensity: number) => {
    setFlapperIntensity(intensity);
    setFlapperTick((t) => t + 1);
  }, []);

  // CRÍTICO: physics tiene que ser memoizado. Si se crea inline en cada
  // render, Wheel detecta cambio en su useEffect → cancela animFrame
  // y reinicia animación → ruleta gira infinito.
  // El bug solo se manifestó al agregar onTick (cada tick = setState =
  // re-render = physics nuevo = restart).
  const physics = useMemo(
    () => ({
      velocidadMin: state?.settings.ruleta_velocidad_minima ?? 0.4,
      variacion: state?.settings.ruleta_variacion_aleatoria ?? 0.6,
      frenado: state?.settings.ruleta_frenado ?? 0.985,
      umbralCasiPasa: state?.settings.ruleta_umbral_casi_pasa ?? 0.2,
    }),
    [
      state?.settings.ruleta_velocidad_minima,
      state?.settings.ruleta_variacion_aleatoria,
      state?.settings.ruleta_frenado,
      state?.settings.ruleta_umbral_casi_pasa,
    ],
  );

  useEffect(() => {
    if (!isAuthenticated) navigate("/setup", { replace: true });
  }, [isAuthenticated, navigate]);

  // Si registrar_datos ON y modo_prueba OFF: pedir datos antes de habilitar girar.
  useEffect(() => {
    if (!state) return;
    // Solo mostrar modal si hay campos efectivos a pedir.
    // Caso edge: toggle ON pero lista vacía = nada que pedir = sin modal.
    const tieneCampos = state.settings.campos_cliente.length > 0;
    if (
      !state.settings.modo_prueba &&
      state.settings.registrar_datos_cliente &&
      tieneCampos &&
      !datos
    ) {
      setNeedsDatos(true);
    }
  }, [state, datos]);

  const handleSpin = useCallback(async () => {
    if (!state || spinning) return;
    setPlayError(null);
    // Sonido de lanzamiento (whoosh). Cascada en audio.ts: archivo
    // por estilo (classic-ruleta-spin-start) → legacy (spin-start) →
    // sintético. Disparado ANTES del request al server para feedback
    // inmediato del click.
    playEvent("ruleta", "spin-start", state.settings.sound_style_slug);
    // Opción B: arrancamos el giro INMEDIATO al click. El Wheel entra
    // en fase 'freespin' (gira libre sin saber el target) mientras
    // esperamos la respuesta del server. Cuando llega, seteamos
    // targetIndex y el Wheel transiciona a frenado cubic ease-out
    // con continuidad de velocidad. Server-authoritative (§8) sigue
    // intacto — el premio lo elige el server, solo cambia *cuándo*
    // recibe el cliente esa info.
    setTargetIndex(-1);
    setResultado(null);
    setSpinning(true);
    try {
      const res = await appPlay({
        game_type: "ruleta",
        customer: datos ?? undefined,
      });
      setResultado(res);
      setTargetIndex(res.position_in_wheel);
    } catch (e) {
      // Si la request falla, cortar el giro inmediatamente y mostrar
      // error. Sin esto la rueda quedaría girando libre para siempre
      // (nunca recibe targetIndex).
      setSpinning(false);
      setPlayError(e instanceof Error ? e.message : String(e));
    }
  }, [state, spinning, datos]);

  const handleSpinEnd = useCallback(() => {
    setSpinning(false);
    setShowPremioModal(true);
  }, []);

  const handleNext = useCallback(() => {
    setShowPremioModal(false);
    setResultado(null);
    setTargetIndex(-1);
    // Si fue reintento, NO limpiamos datos del cliente: el cliente
    // sigue siendo el mismo y debe poder jugar inmediatamente sin
    // re-cargar nombre/teléfono/etc. Si fue premio real o consolation,
    // limpiamos para que el próximo cliente arranque desde cero.
    if (!resultado?.is_reintento) {
      setDatos(null);
    }
  }, [resultado]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin opacity-50" />
      </main>
    );
  }

  if (error || !state) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-red-400">No se pudo cargar.</p>
        <button onClick={() => navigate("/")} className="rounded-lg bg-white/10 px-4 py-2">
          Volver
        </button>
      </main>
    );
  }

  if (!state.settings.ruleta_habilitada) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p>Ruleta no habilitada en esta sucursal.</p>
        <button onClick={() => navigate("/")} className="rounded-lg bg-white/10 px-4 py-2">
          Volver
        </button>
      </main>
    );
  }

  const premios = state.premios.ruleta;
  if (premios.length === 0) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p>Aún no hay premios cargados para la ruleta.</p>
        <button onClick={() => navigate("/")} className="rounded-lg bg-white/10 px-4 py-2">
          Volver
        </button>
      </main>
    );
  }

  // Imagen de la campaña dentro del juego (header). Migration 034/035: alto +
  // aire arriba configurables desde el admin.
  const campana = state.campana;
  const campanaHeight = state.global.campana_juego_height_px;
  const campanaTopMargin = state.global.campana_juego_top_margin_px;

  // La pantalla se dibuja sobre el lienzo 1080x1920 (FitScreen), igual que el
  // landing, con el markup COMPARTIDO RuletaView -> el preview del admin
  // (VirtualScreen + RuletaView) coincide 100% a cualquier resolucion. Los
  // overlays a pantalla completa (banner de modo prueba + modales) van AFUERA
  // del lienzo escalado: si quedaran adentro, el transform de FitScreen los
  // anclaria al lienzo en vez del viewport (modal chico/descentrado).
  return (
    <>
      {state.settings.modo_prueba && (
        <div className="fixed left-0 top-0 z-[60] w-full">
          <ModoPruebaBanner />
        </div>
      )}

      <FitScreen bgStyle={bgStyle}>
        <RuletaView
          campana={campana}
          campanaHeightPx={campanaHeight}
          campanaTopMarginPx={campanaTopMargin}
          premios={premios}
          colorBorde={state.settings.ruleta_color_borde}
          colorCentro={state.settings.ruleta_color_centro}
          grosorDivisoria={state.settings.ruleta_grosor_divisoria}
          textAlign={state.settings.ruleta_text_align}
          verticalPosPct={state.global.juego_vertical_pos_pct}
          soundStyle={state.settings.sound_style_slug}
          dprMax={state.global.juego_canvas_dpr_max}
          wheelSize={state.global.ruleta_wheel_size_px}
          physics={physics}
          spinning={spinning}
          targetIndex={targetIndex}
          onSpinEnd={handleSpinEnd}
          onTick={handleTick}
          flapperTick={flapperTick}
          flapperIntensity={flapperIntensity}
          onSpin={handleSpin}
          spinDisabled={
            spinning ||
            (state.settings.registrar_datos_cliente &&
              state.settings.campos_cliente.length > 0 &&
              !datos &&
              !state.settings.modo_prueba)
          }
          playError={playError}
          onBack={() => navigate("/")}
        />
      </FitScreen>

      <Modal open={needsDatos}>
        <DatosClienteForm
          camposVisibles={state.settings.campos_cliente}
          camposObligatorios={state.settings.campos_obligatorios}
          onSubmit={(d) => {
            setDatos(d);
            setNeedsDatos(false);
          }}
        />
      </Modal>

      <PremioModal
        open={showPremioModal && resultado !== null}
        premioNombre={resultado?.prize.nombre ?? ""}
        isConsolation={resultado?.is_consolation ?? false}
        isReintento={resultado?.is_reintento ?? false}
        soundStyle={state.settings.sound_style_slug}
        gameType="ruleta"
        onNext={handleNext}
        onMainMenu={() => navigate("/")}
      />
    </>
  );
}
